import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useField, FormRenderProps } from 'react-final-form';

import { b } from 'utils/i18nUtils';
import { formatUndefined } from 'utils/formUtils';

import PageBaseProps from 'types/PageBase';
import { YesNoChoice } from 'types/Question';

import { isVehicleCheckEnabled as isVehicleCheckEnabledSelector } from 'modules/auth/selectors';
import { isCourtesyVehicleCheckEnabled as isCourtesyVehicleCheckEnabledSelector } from 'modules/selfServices/selectors';

import Card from 'components/ui/Card';
import PageFooter from 'components/ui/PageFooter';

import YesNoCardActions from '../ui/Card/YesNoCardActions';

type VehicleCheckFormProps = FormRenderProps & PageBaseProps;

const VehicleCheckFormRender: React.FC<VehicleCheckFormProps> = ({ handleSubmit, hasValidationErrors, onPrev, shouldDisplayBackButton }) => {
  const intl = useIntl();
  const isVehicleCheckEnabled = useSelector(isVehicleCheckEnabledSelector);
  const isCourtesyVehicleCheckEnabled = useSelector(isCourtesyVehicleCheckEnabledSelector);

  const {
    input:{
      value: status,
      onChange: handleChangeStatus,
    },
    meta:vehicleCheckMeta
  } = useField<YesNoChoice, HTMLElement>('vehicleCheck', {
    format: formatUndefined,
    subscription: { value: true, error: true, submitFailed: true },
  });

  const {
    input:{
      value: mobilityStatus,
      onChange: handleChangeMobilityStatus,
    },
    meta:courtesyVehicleCheckMeta
  } = useField<YesNoChoice, HTMLElement>('courtesyVehicleCheck', {
    format: formatUndefined,
    subscription: { value: true },
  });

  const vehicleCheckError = vehicleCheckMeta.submitFailed && vehicleCheckMeta.error;
  const courtesyVehicleCheckMetaError = courtesyVehicleCheckMeta.submitFailed && courtesyVehicleCheckMeta.error;

  const vehicleCheckLabel = React.useMemo(() => intl.formatMessage({
    id: `vehicleCheck.selfService.${isCourtesyVehicleCheckEnabled ? 'personalQuestion' : 'modalQuestion'}`,
    defaultMessage: isCourtesyVehicleCheckEnabled
      ? 'Do you want to do a check of your personal vehicle yourself?'
      : 'Do you want to do a check yourself?',
  }), [intl, isCourtesyVehicleCheckEnabled]);

  return (
    <div className="main-content">
      <main className="flex flex-col text-center kiosk:px-16">
        <span className="mb-10">
          <FormattedMessage
            id="vehicleCheck.selfService.question"
            defaultMessage="Would you like to do a <b>guided vehicle check?</b>"
            values={{ b }}
          />
        </span>
        <span className="mb-10">
          <FormattedMessage
            id="vehicleCheck.selfService.information"
            defaultMessage="We will ask you to take <b>photos with your own smartphone</b> at the end of the check-in."
            values={{ b }}
          />
        </span>
        <FormattedMessage
          id="vehicleCheck.selfService.responsability"
          // eslint-disable-next-line max-len
          defaultMessage="By taking a moment to document the vehicle’s condition now, you avoid being held responsible for any damage that has not been previously reported."
          tagName="span"
        />
        <ul className="w-full max-w-content kiosk:border-t-2 border-border-default mb-20 kiosk:pt-20">
          {isVehicleCheckEnabled && (
            <li>
              <Card
                mandatory
                centerText
                title={vehicleCheckLabel}
                className="mb-5 kiosk:mb-20"
                error={vehicleCheckError}
              >
                <YesNoCardActions onChange={handleChangeStatus} value={status} />
              </Card>
            </li>
          )}
          {isCourtesyVehicleCheckEnabled && (
            <li>
              <Card
                mandatory
                centerText
                error={courtesyVehicleCheckMetaError}
                title={
                  intl.formatMessage({
                    id: 'vehicleCheck.selfService.courtesyQuestion',
                    defaultMessage: 'Do you want to do a check of the courtesy vehicle yourself?'
                  })
                }
              >
                <YesNoCardActions onChange={handleChangeMobilityStatus} value={mobilityStatus} />
              </Card>
            </li>
          )}
        </ul>
      </main>
      <PageFooter
        onPrev={onPrev}
        onNext={handleSubmit}
        disabledStyle={hasValidationErrors}
        shouldDisplayBackButton={shouldDisplayBackButton}
      />
    </div>
  );
};

export default VehicleCheckFormRender;
